import { Text } from '@mantine/core';
import { Group, Menu, Portal, Tooltip } from '@mantine/core';
import { IconDotsVertical, IconPencil, IconTrashX } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import panic from 'errors/panic';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { EDIT_MEASURING_DEVICE_PAGE_PATH } from 'routes/paths';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

/**
 * Interface for MeasuringDeviceActions component props
 */
export interface MeasuringDeviceActionsProps {
  measuringDeviceId: number;
  measuringDeviceName: string;
  compactOnTablet?: boolean;
  onDelete: () => void;
}

/**
 * Displays actions for a measuring device in a table.
 */
export default function MeasuringDeviceActions({
  measuringDeviceId,
  measuringDeviceName,
  compactOnTablet = false,
  onDelete,
}: MeasuringDeviceActionsProps) {
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile';

  /**
   * Makes a call to the API to delete the measuring device.
   */
  const deleteMeasuringDevice = useCallback(() => {
    const measuringDeviceDelete = getAction('MeasuringDeviceDelete');

    measuringDeviceDelete({ parameters: { measuringDeviceId: String(measuringDeviceId) } })
      .then(() => {
        onDelete();
      })
      .catch(panic);
  }, [getAction, measuringDeviceId]);

  /**
   * Confirms the deletion of the measuring device.
   */
  const confirmDeleteMeasuringDevice = useCallback(() => {
    confirm({
      title: 'Vymazať merací prístroj',
      content: `Naozaj chcete vymazať merací prístroj ${measuringDeviceName}?`,
      onConfirm: deleteMeasuringDevice,
    });
  }, [confirm, deleteMeasuringDevice]);

  return (
    <Group spacing={12} noWrap>
      {!menuOnly &&
        (isCompact ? (
          <Tooltip label="Editovať" events={{ hover: true, focus: true, touch: true }}>
            <ActionIcon
              variant="secondary"
              size="md"
              component={Link}
              to={EDIT_MEASURING_DEVICE_PAGE_PATH.insert({ measuringDeviceId })}
            >
              <IconPencil />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Button
            size="md"
            component={Link}
            to={EDIT_MEASURING_DEVICE_PAGE_PATH.insert({ measuringDeviceId })}
            variant="secondary"
            leftIcon={<IconPencil />}
          >
            Editovať
          </Button>
        ))}
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
            <IconDotsVertical stroke="1.5" />
          </ActionIcon>
        </Menu.Target>
        <Portal>
          <Menu.Dropdown>
            <Menu.Label>
              <Text maw={160} truncate>
                {measuringDeviceName}
              </Text>
            </Menu.Label>
            {menuOnly && (
              <Menu.Item
                component={Link}
                to={EDIT_MEASURING_DEVICE_PAGE_PATH.insert({ measuringDeviceId })}
                icon={<IconPencil stroke={1.5} size={20} />}
              >
                Editovať
              </Menu.Item>
            )}
            <Menu.Item
              color="red.8"
              onClick={confirmDeleteMeasuringDevice}
              icon={<IconTrashX stroke={1.5} size={20} />}
            >
              Vymazať
            </Menu.Item>
          </Menu.Dropdown>
        </Portal>
      </Menu>
    </Group>
  );
}
